import { useState } from 'react';
import TextInputField from 'components/TextInputField';
import ActionButton from 'components/ActionButton';
import StatusMessage from 'components/StatusMessage';
import CountrySelect from 'components/CountrySelect';
import { useSaveAddressMutation } from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { trackEvent, getUserProperties } from 'utils/analytics';
import '../components/LoginForm/forms/JoinWaitlistTab/JoinWaitlistTab.css';

type Props = {
  onNext: () => void;
};

const AddressStep = ({ onNext }: Props) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });
  const [saveAddress, { isLoading, error }] = useSaveAddressMutation();
  const { data: userData } = useGetMeQuery();

  const isValid =
    form.firstName &&
    form.lastName &&
    form.address1 &&
    form.city &&
    form.state &&
    form.postalCode &&
    form.country;

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFinish = async () => {
    if (!isValid) return;

    try {
      const addressData = {
        firstName: form.firstName,
        lastName: form.lastName,
        address1: form.address1,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        country: form.country,
        ...(form.address2 && { address2: form.address2 }), // Only include if not empty
      };

      await saveAddress(addressData).unwrap();

      // Track shipping address added in GA4
      trackEvent('shipping_address_added', {
        ...getUserProperties({
          id: userData?.id,
          createdAt: userData?.createdAt,
          country: form.country, // Use country from the form
        }),
        country: form.country, // Override with selected country
      });

      // Longer delay to ensure cookies are fully set after address save
      // This is critical for cookie authentication on staging/prod
      // Increased from 100ms to 300ms for better reliability
      await new Promise(resolve => setTimeout(resolve, 300));

      onNext();
    } catch (err: any) {
      // Log error without sensitive details
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save address - Status:', err?.status);
      }
      // Error is handled by the error state from the mutation
    }
  };

  return (
    <div className="login-form wide">
      <div className="login-form-content join-waitlist">
        <div className="onboarding-progress-dots">
          <div className="dot" />
          <div className="dot" />
          <div className="dot" />
          <div className="dot active" />
        </div>

        <h1 className="join-waitlist-title">Shipping address</h1>
        <p className="join-waitlist-description" style={{ marginTop: 0 }}>
          Members who mail you cards will send them to this location.
        </p>

        <div className="join-waitlist-input">
          <>
            <div className="onboarding-row">
              <TextInputField
                placeholder="First Name"
                value={form.firstName}
                onChange={(val) => handleChange('firstName', val)}
              />
              <TextInputField
                placeholder="Last Name"
                value={form.lastName}
                onChange={(val) => handleChange('lastName', val)}
              />
            </div>
            
            <TextInputField
              placeholder="Address Line 1"
              value={form.address1}
              onChange={(val) => handleChange('address1', val)}
            />
            <TextInputField
              placeholder="Address Line 2"
              value={form.address2}
              onChange={(val) => handleChange('address2', val)}
            />

            <div className="onboarding-row">
              <TextInputField
                placeholder="City"
                value={form.city}
                onChange={(val) => handleChange('city', val)}
              />
              <TextInputField
                placeholder="State / Province / Region"
                value={form.state}
                onChange={(val) => handleChange('state', val)}
              />
            </div>

            <div className="onboarding-row">
              <TextInputField
                placeholder="Postal Code"
                value={form.postalCode}
                onChange={(val) => handleChange('postalCode', val)}
              />
              <CountrySelect
                value={form.country}
                onChange={(val) => handleChange('country', val)}
              />
            </div>

            <ActionButton
              label="Finish"
              onPress={handleFinish}
              isLoading={isLoading}
              isDisabled={!isValid}
            />
            
            {error && (
              <StatusMessage 
                type="error" 
                message={
                  (error as any).data?.message || 
                  `Failed to save address. Status: ${(error as any).status}. Please try again.`
                } 
              />
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
